import { McpTokenScope } from 'Apis/library';

export type McpSkill = {
    /** Directory name and skill identifier. Must match the `name` in the frontmatter. */
    name: string;
    title: string;
    /** One line shown in the catalog row, before anyone expands the body. */
    summary: string;
    /** Scopes the token must carry for this skill's tools to be listed at all. */
    scopes: McpTokenScope[];
    markdown: string;
};

/**
 * Joins authored lines into a SKILL.md body.
 *
 * The content is held as single-quoted lines rather than a template literal so
 * markdown code fences and inline spans keep their backticks without escaping.
 */
const md = (...lines: string[]): string => lines.join('\n');

const POSTURE_REVIEW = md(
    '---',
    'name: amplifier-posture-review',
    'description: Produce a workforce security posture review from Amplifier data — population, coverage, findings, and where risk concentrates. Use when asked for a posture review, a security summary, or a leadership update.',
    '---',
    '',
    '# Amplifier posture review',
    '',
    'Amplifier holds aggregated workforce security data for your tenant: per-user tooling coverage and security findings. This skill turns that into a review someone can act on.',
    '',
    '## Before you query',
    '',
    'Call `get_insights_context` first, every session. It returns the datasets (`user_coverage`, `risk_advisor`) with the exact filters, group-by fields, and metric fields that are allowed. A filter outside that list is rejected rather than ignored, so guessing costs you a turn.',
    '',
    '## Shape of the review',
    '',
    '1. **Scale.** `aggregate_insights` on `user_coverage` grouped by `organization`. This is your denominator, and every later number is unreadable without it.',
    '2. **Findings by severity.** `aggregate_insights` on `risk_advisor` grouped by `severity`, then `status`. Open critical and high is the headline.',
    '3. **Where it concentrates.** The same aggregation grouped by `department`, then `severity`. Two levels is the server maximum.',
    '4. **Named examples.** Only now call `query_risk_advisor` for a handful of representative findings in the worst bucket. Keep `limit` small.',
    '',
    '## Rules',
    '',
    '- Prefer `aggregate_insights` over paging rows. It counts server-side and keeps per-user records out of the conversation.',
    '- Pass `purpose` on every call. It is written to the tenant audit trail and is how an admin later reconciles what the model looked at.',
    '- Never state a number a tool did not return. If a result is marked `partial`, say so in the review.',
    '- Report rates next to counts. "412 users without MFA" means nothing without the population.',
    '- This connection is read only. Close with recommended actions for a human, never claimed fixes.',
    '',
    '## Output',
    '',
    'Lead with the three things that matter most or that changed. Then one short table per section. Close with specific next actions and who owns each.',
    '',
);

const COVERAGE_GAPS = md(
    '---',
    'name: amplifier-coverage-gaps',
    'description: Find coverage and asset attribution gaps in Amplifier — users missing MFA, endpoint protection or training, assets attributed to the wrong person, assets owned by nobody, assets claimed by several people across providers, and users with no assets at all. Use when asked who is uncovered, whose device this really is, or where attribution is wrong.',
    '---',
    '',
    '# Coverage and attribution gaps',
    '',
    "The `user_coverage` dataset is one document per user carrying that user's tooling state: MFA posture, identity profile, device attribution, training, and endpoint and vulnerability coverage.",
    '',
    'Attribution questions need a second surface. `saas_assets` on the guarded SQL tool holds **one row per asset per connector**, so the same laptop appears once per provider that reported it. The columns that carry attribution:',
    '',
    '- `uid` — the owning user. **Nullable**, and null is exactly the ownerless case.',
    '- `aid` — the global asset the row was reconciled onto, which is what ties the same device together across providers. Also nullable: a row that never reconciled has no `aid` and cannot be grouped with its twins.',
    '- `cid` — the connector the row came from.',
    '',
    'Counting rows in `saas_assets` is not counting assets. Count distinct `aid`, and say which you did.',
    '',
    '## Start here, in this order',
    '',
    '1. `get_insights_context` — read the `user_coverage` filter list. Field names rarely match what you would guess; use the ones it returns.',
    '2. Establish the denominator: `aggregate_insights` on `user_coverage` with no filters, grouped by `organization`.',
    '3. Answer whatever you can from `user_coverage`. It is aggregated server-side and costs one call.',
    '4. Drop to `run_analytics_query` only for the asset-shaped questions below, which the insights datasets do not model.',
    '',
    '## The four attribution questions',
    '',
    '### An asset that may not actually be theirs',
    '',
    '`query_user_coverage` with `filters: { "attribution.status": "NEGATIVE" }`. NEGATIVE means at least one of that user\'s assets carries conflicting attribution evidence — the platform linked it to them and something disagrees. It is a flag to investigate, not a verdict.',
    '',
    'For the distribution rather than the list, `aggregate_insights` on `user_coverage` grouped by `attributionStatus`. Passing both statuses in one filter matches everything and tells you nothing.',
    '',
    '### A user with no assets',
    '',
    '`query_user_coverage` with `filters: { "device": "missing" }`. `connected`, `missing`, and `multiple` are the only accepted values. Read `missing` carefully before reporting it: it means no asset reached Amplifier for that person, which is either a real coverage gap or a connector that does not cover their device type.',
    '',
    '### An asset nobody owns',
    '',
    'Guarded SQL. Group by connector so the answer names who has to fix it:',
    '',
    '```sql',
    'SELECT sa.cid, COUNT(*) AS orphan_rows',
    'FROM saas_assets sa',
    'WHERE sa.uid IS NULL AND sa.deletedAt IS NULL',
    'GROUP BY sa.cid',
    '```',
    '',
    '### An asset claimed by several people across providers',
    '',
    'The same `aid` reached by rows with different `uid`s. That is the cross-provider conflict:',
    '',
    '```sql',
    'SELECT sa.aid,',
    '       COUNT(DISTINCT sa.uid) AS owners,',
    '       COUNT(DISTINCT sa.cid) AS providers',
    'FROM saas_assets sa',
    'WHERE sa.aid IS NOT NULL AND sa.deletedAt IS NULL',
    'GROUP BY sa.aid',
    'HAVING COUNT(DISTINCT sa.uid) > 1',
    '```',
    '',
    'Rows disagreeing across two connectors is the ordinary case worth reporting. Rows disagreeing inside one connector is a different and worse problem — say which you found.',
    '',
    '## Rules that will bite you',
    '',
    '- **`deletedAt IS NULL` is yours to add.** The guard scopes every query to the tenant automatically; it does not filter soft-deleted rows. Leave it off and you are counting decommissioned hardware.',
    '- **No subqueries.** A `SELECT` inside `WHERE` or inside a projected column is rejected. Use a `WITH` clause; it does everything a subquery would here.',
    '- Do not infer coverage from the absence of a finding. Query the coverage dataset.',
    '- An unknown filter key is rejected outright, never ignored. If a query fails, re-read the context instead of retrying variations.',
    '- Pass `purpose` on every call.',
    '- Read only: propose the remediation, do not describe it as applied.',
    '',
    '## Prioritization',
    '',
    'A gap matters in proportion to blast radius, not headcount:',
    '',
    '- Privileged and admin populations before general staff.',
    '- Gaps concentrated in one department or one connector before gaps spread evenly — concentration means one owner and one fix.',
    '- Gaps that compound. No MFA plus no endpoint coverage on the same user is not two problems, it is one open path.',
    '- Wrong attribution outranks missing attribution. An unowned asset is a gap someone can close; an asset attributed to the wrong person means findings are routed to someone who cannot act on them, and the real owner never hears about it.',
    '',
);

const FINDINGS_TRIAGE = md(
    '---',
    'name: amplifier-findings-triage',
    'description: Triage open Amplifier security findings — group them by cause, prioritize, and batch the work by owner. Use when asked what to fix first, what is overdue, or to work a findings queue.',
    '---',
    '',
    '# Findings triage',
    '',
    'The `risk_advisor` dataset holds security findings with severity, status, category, owning user and asset, and vulnerability counts.',
    '',
    '## Method',
    '',
    '1. `get_insights_context` for the `risk_advisor` filters and group-by fields.',
    '2. `aggregate_insights` grouped by `status`, then `severity`. That is the backlog shape, and it comes before reading any individual finding.',
    '3. Aggregate again by `category` to surface systemic causes. Fifty findings from one misconfiguration is one piece of work, not fifty.',
    '4. `query_risk_advisor` for the top bucket only.',
    '5. For a named person, `list_user_findings` with their `uid` returns their queue directly.',
    '',
    '## Triage order',
    '',
    'Work causes before instances:',
    '',
    '1. Open critical findings on privileged users or internet-reachable assets.',
    '2. The categories with the highest counts — one fix closes many.',
    '3. Anything trending up since the last review.',
    '4. Everything else, batched by owner so a person receives one message rather than twenty.',
    '',
    '## Rules',
    '',
    '- A finding count is not a risk score. Say what an attacker gains, not how many rows came back.',
    '- Do not de-duplicate on title alone. The same title on two assets is two pieces of work.',
    '- Pass `purpose` on every call; it is recorded in the tenant audit trail.',
    '- Read only. You produce the triage plan, a human executes it.',
    '',
);

const ANALYTICS_QUERY = md(
    '---',
    'name: amplifier-analytics-query',
    'description: Answer ad-hoc questions about Amplifier tenant data with guarded SQL. Use when the insights datasets do not cover the question and the analytics tables need to be queried directly.',
    '---',
    '',
    '# Guarded analytics queries',
    '',
    "Amplifier exposes a read-only SQL surface over your tenant's analytics tables. Every query is tenant-scoped by the server, hashed, and written to your audit trail.",
    '',
    '## Always first',
    '',
    'Call `get_analytics_context`. It returns the schema you may query and the guardrails your SQL has to satisfy. SQL written before reading it is a wasted turn — anything outside the allowed shape is rejected.',
    '',
    '## Writing the query',
    '',
    '- Select the columns you need. `SELECT *` returns width you will not use and spends the row cap on it.',
    '- Aggregate in SQL. `GROUP BY` and `COUNT` in the database beats pulling rows and counting them in the conversation.',
    '- **No subqueries.** A `SELECT` nested in a `WHERE` clause or a projected column is rejected outright. Express it as a `WITH` clause instead — CTEs are supported and tenant-scoped on every branch.',
    '- Soft-deleted rows are still there. The guard scopes to your tenant for you; filtering `deletedAt IS NULL` is your job.',
    '- Set `limit` explicitly. The default is 200 and the ceiling is 500.',
    '- Pass `purpose`. One line, recorded against the query hash.',
    '',
    '## Reading the result',
    '',
    'The `meta` block carries `rowCount`, `returnedRowCount`, and `partial`. When `partial` is true you are holding a slice — narrow the query, or state plainly that the answer rests on a truncated set.',
    '',
    '## Rules',
    '',
    '- Read only, by construction. There is no write path on this connection; do not offer one.',
    '- Do not invent a column absent from the context response, and do not retry a rejected query with variations. Re-read the schema.',
    '- When the question is about coverage or findings, prefer the insights tools. They aggregate server-side and are faster.',
    '',
);

/**
 * Skills a customer can drop into their own Claude configuration (APD-1818).
 *
 * These are authored here rather than fetched because they describe the tool
 * contract this build ships: a skill that names a filter or a cap the server no
 * longer honours is worse than no skill, so the text moves with the client.
 */
export const MCP_SKILLS: readonly McpSkill[] = [
    {
        name: 'amplifier-posture-review',
        title: 'Posture review',
        summary: 'Turns coverage and findings into a review with population, concentration, and named actions.',
        scopes: ['amp.insights.read'],
        markdown: POSTURE_REVIEW,
    },
    {
        name: 'amplifier-coverage-gaps',
        title: 'Coverage and attribution gaps',
        summary:
            'Finds missing tooling plus the four attribution failures: wrong owner, no owner, several owners across providers, and users with no assets.',
        scopes: ['amp.insights.read', 'amp.analytics.read'],
        markdown: COVERAGE_GAPS,
    },
    {
        name: 'amplifier-findings-triage',
        title: 'Findings triage',
        summary: 'Groups the findings backlog by cause, orders the work, and batches it by owner.',
        scopes: ['amp.insights.read', 'amp.findings.read'],
        markdown: FINDINGS_TRIAGE,
    },
    {
        name: 'amplifier-analytics-query',
        title: 'Analytics queries',
        summary: 'Teaches the guarded SQL surface: read the schema first, aggregate in the database, respect the caps.',
        scopes: ['amp.analytics.read'],
        markdown: ANALYTICS_QUERY,
    },
];

/** The path a skill is saved to, shown in the catalog and used by the install snippet. */
export const skillPath = (skill: McpSkill): string => `~/.claude/skills/${skill.name}/SKILL.md`;
